import type { CalendarEvent } from "../../shared/types.js";

function fmtForGcal(iso: string, allDay: boolean): string {
  if (allDay) return iso.replace(/-/g, "");
  const d = new Date(iso);
  const pad = (n: number) => n.toString().padStart(2, "0");
  return (
    d.getUTCFullYear().toString() +
    pad(d.getUTCMonth() + 1) +
    pad(d.getUTCDate()) +
    "T" +
    pad(d.getUTCHours()) +
    pad(d.getUTCMinutes()) +
    pad(d.getUTCSeconds()) +
    "Z"
  );
}

function endFor(ev: CalendarEvent): string {
  if (ev.end) return ev.end;
  // Default 1-hour duration if no end given.
  if (ev.allDay) {
    const d = new Date(ev.start + "T00:00:00Z");
    d.setUTCDate(d.getUTCDate() + 1);
    return d.toISOString().slice(0, 10);
  }
  return new Date(Date.parse(ev.start) + 60 * 60 * 1000).toISOString();
}

export function googleCalendarUrl(ev: CalendarEvent): string {
  const start = fmtForGcal(ev.start, ev.allDay);
  const end = fmtForGcal(endFor(ev), ev.allDay);
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: `[${ev.sourceName}] ${ev.title}`,
    dates: `${start}/${end}`,
  });
  if (ev.description) params.set("details", ev.description);
  if (ev.location) params.set("location", ev.location);
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

export function singleEventIcsUrl(ev: CalendarEvent): string {
  // We don't have a per-event endpoint, but the merged feed will contain it.
  // For "download .ics" we could do a data: URL of just this event.
  const ics = singleEventIcs(ev);
  return `data:text/calendar;charset=utf-8,${encodeURIComponent(ics)}`;
}

function singleEventIcs(ev: CalendarEvent): string {
  const start = fmtForGcal(ev.start, ev.allDay);
  const end = fmtForGcal(endFor(ev), ev.allDay);
  const stamp = fmtForGcal(new Date().toISOString(), false);
  const esc = (s: string) =>
    s.replace(/\\/g, "\\\\").replace(/\n/g, "\\n").replace(/,/g, "\\,").replace(/;/g, "\\;");
  return [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//alohah3site//v1//EN",
    "BEGIN:VEVENT",
    `UID:${ev.id}@alohah3site`,
    `DTSTAMP:${stamp}`,
    ev.allDay ? `DTSTART;VALUE=DATE:${start}` : `DTSTART:${start}`,
    ev.allDay ? `DTEND;VALUE=DATE:${end}` : `DTEND:${end}`,
    `SUMMARY:${esc(`[${ev.sourceName}] ${ev.title}`)}`,
    ev.description ? `DESCRIPTION:${esc(ev.description)}` : "",
    ev.location ? `LOCATION:${esc(ev.location)}` : "",
    "END:VEVENT",
    "END:VCALENDAR",
    "",
  ]
    .filter(Boolean)
    .join("\r\n");
}
